import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import { SinistriDeclaration, sinistriValid } from "@/components/SinistriDeclaration";
import { IddDeclaration, iddValid } from "@/components/IddDeclaration";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyConsent, LegalFooterLinks } from "@/components/LegalConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);

// Mappa "categoria assicurati" del link pubblico → codice categoria del motore Infortuni
const INF_CAT_MAP = {
  "Personale dipendente (subordinato/parasubordinato, tirocinanti)": "dipendenti",
  "Collaboratori / personale occasionale": "collaboratori",
  "Clero (parroci, sacerdoti, suore, laici)": "clero",
  "Soci e amministratori": "soci_amministratori",
  "Utenti / assistiti / ospiti / partecipanti": "utenti",
  "Sport (atleti / tesserati)": "sport",
};

export default function PublicIntake() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);

  const rc = info?.schema?.rc;
  const inf = info?.schema?.inf;
  const infCat = inf ? inf.categories.find((c) => c.code === INF_CAT_MAP[values.categoria_assicurati]) : null;

  useEffect(() => {
    axios.get(`${API}/public/intake/${token}`)
      .then(({ data }) => {
        setInfo(data);
        if (data.already_submitted) setDone(true);
        if (data.schema?.rc) {
          setValues((v) => ({
            ...v,
            ambito: "non_sanitaria",
            sezione_rischio: "non_sanitaria",
            parametro: "fatturato",
            settore_principale: "",
            tipo_principale: "",
            massimale: String(data.schema.rc.sections["non_sanitaria"].massimali[0]),
            classe_rischio: "1",
          }));
        }
      })
      .catch(() => setInfo({ error: true }))
      .finally(() => setLoading(false));
  }, [token]);

  const setV = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  // Infortuni: alla scelta della categoria, imposta i default delle garanzie obbligatorie e ripulisce le stale
  useEffect(() => {
    if (!infCat) return;
    const valid = new Set(infCat.coverages.map((c) => c.code));
    setValues((v) => {
      const nv = { ...v };
      Object.keys(nv).forEach((k) => { if (k.startsWith("garanzia_") && !valid.has(k.slice(9))) delete nv[k]; });
      infCat.coverages.forEach((c) => {
        if (c.obbligatoria && nv[`garanzia_${c.code}`] == null)
          nv[`garanzia_${c.code}`] = c.massimali[Math.floor(c.massimali.length / 2)] || c.massimali[0];
      });
      return nv;
    });
  }, [infCat]);

  // RC: sezione corrente + dipendenze
  const rcSection = rc && values.sezione_rischio ? rc.sections[values.sezione_rischio] : null;
  const rcParamMeta = rcSection?.parameters?.[values.parametro];
  const nonsanSettori = rc?.nonsan_settori || [];
  const sportTipi = rc?.nonsan_sport_tipi || [];
  const genericTipi = rc?.nonsan_generic_tipi || [];
  const sanSettori = rc?.sanitary_settori || [];
  const sanTipi = rc?.sanitary_tipi || [];
  const settoreMode = (nonsanSettori.find((s) => s.label === values.settore_principale) || {}).param_mode || null;
  const sanTipoMeta = sanTipi.find((t) => t.code === values.tipo_principale) || null;

  // Ammissibilità garanzie opzionali per Settore Principale (Norme Tariffarie)
  const covElig = rc?.coverage_eligibility || {};
  const isCovEligible = (coverageCode) => {
    const rule = covElig[coverageCode];
    if (!rule) return true;
    if (rule.block_sanitaria && values.ambito === "sanitaria") return false;
    const s = values.settore_principale || "";
    if (!s) return false;
    if (rule.mode === "allow_list") return (rule.settori || []).includes(s);
    if (rule.mode === "block_list") return !(rule.settori || []).includes(s);
    return true;
  };

  // Deseleziona automaticamente le garanzie non ammesse quando cambia il soggetto
  useEffect(() => {
    if (!rc) return;
    setValues((v) => {
      let changed = false;
      const nv = { ...v };
      (rc.coverages || []).forEach((c) => {
        if (c.coverage_code && nv[c.name] === "Sì" && !isCovEligible(c.coverage_code)) {
          nv[c.name] = ""; changed = true;
        }
      });
      return changed ? nv : v;
    });
  }, [values.settore_principale, values.ambito, rc]);

  const rcSetSezioneParam = (sez, param) => {
    setValues((v) => {
      const s = rc.sections[sez];
      const massOk = s.massimali.includes(Number(v.massimale));
      return { ...v, sezione_rischio: sez, parametro: param, massimale: massOk ? v.massimale : String(s.massimali[0]) };
    });
  };

  const onAmbito = (v) => {
    setValues((s) => ({ ...s, ambito: v, settore_principale: "", tipo_principale: "" }));
    rcSetSezioneParam(v === "non_sanitaria" ? "non_sanitaria" : "sanitaria_basso_rischio", "fatturato");
  };

  const onSettore = (label) => {
    setValues((s) => ({ ...s, settore_principale: label, tipo_principale: "" }));
    if (values.ambito === "non_sanitaria") {
      const mode = (nonsanSettori.find((x) => x.label === label) || {}).param_mode;
      rcSetSezioneParam("non_sanitaria", mode === "tesserati" ? "tesserati" : mode === "anime" ? "anime" : "fatturato");
    }
  };

  const onTipo = (val) => {
    if (values.ambito === "sanitaria") {
      const t = sanTipi.find((x) => x.code === val);
      setValues((s) => ({ ...s, tipo_principale: val }));
      if (t) rcSetSezioneParam(t.section, "fatturato");
    } else if (settoreMode === "tesserati") {
      const t = sportTipi.find((x) => x.code === val);
      setValues((s) => ({ ...s, tipo_principale: val, classe_rischio: t ? t.risk_class : s.classe_rischio || "1" }));
    } else {
      setValues((s) => ({ ...s, tipo_principale: val }));
    }
  };

  const resolveTipoLabel = () => {
    if (values.ambito === "sanitaria") return sanTipoMeta?.label || "";
    if (settoreMode === "tesserati") return sportTipi.find((x) => x.code === values.tipo_principale)?.label || "";
    if (settoreMode === "anime") return rc?.nonsan_parrocchie_tipo || "";
    return values.tipo_principale || "";
  };

  const submit = async () => {
    const required = info.schema.sections.flatMap((s) => s.fields)
      .filter((f) => f.required && (!f.show_if || values[f.show_if.field] === f.show_if.value));
    for (const f of required) {
      if (!values[f.name]) return toast.error(`Compila: ${f.label}`);
    }
    if (rc) {
      if (!values.settore_principale) return toast.error("Seleziona il Settore Principale");
      const needTipo = values.ambito === "sanitaria" || settoreMode === "tesserati" || settoreMode === "choice";
      if (needTipo && !values.tipo_principale) return toast.error("Seleziona il Tipo Principale (attività)");
      if (!values.parametro_valore) return toast.error(`Indica: ${rcParamMeta?.label || "il valore richiesto"}`);
      if (!values.massimale) return toast.error("Seleziona il massimale");
    }
    if (!sinistriValid(values.sinistri)) return toast.error("Compila la dichiarazione sinistri e conferma la veridicità");
    if (info.schema.idd && !iddValid(values.idd)) return toast.error("Compila la valutazione di adeguatezza (IDD)");
    if (!privacyOk) return toast.error("Per procedere devi accettare l'informativa privacy");
    setSending(true);
    try {
      const payload = { ...values };
      if (rc) { payload.settore_principale = values.settore_principale; payload.tipo_principale = resolveTipoLabel(); }
      await axios.post(`${API}/public/intake/${token}`, { data: payload });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally { setSending(false); }
  };

  const renderField = (f) => {
    if (f.show_if && values[f.show_if.field] !== f.show_if.value) return null;
    return (
    <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label>{f.label}{f.required && <span className="text-terracotta"> *</span>}</Label>
      {f.type === "textarea" ? (
        <Textarea data-testid={`intake-field-${f.name}`} value={values[f.name] || ""} onChange={(e) => setV(f.name, e.target.value)} rows={3} />
      ) : f.type === "select" ? (
        <Select value={values[f.name] || ""} onValueChange={(v) => setV(f.name, v)}>
          <SelectTrigger data-testid={`intake-field-${f.name}`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
          <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input data-testid={`intake-field-${f.name}`} type={f.type === "number" ? "number" : f.type === "email" ? "email" : f.type === "date" ? "date" : "text"}
          value={values[f.name] || ""} onChange={(e) => setV(f.name, e.target.value)} />
      )}
    </div>
    );
  };

  const YesNo = ({ name, label }) => (
    <div className="sm:col-span-2 space-y-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        {["Sì", "No"].map((opt) => (
          <button key={opt} type="button" data-testid={`intake-yesno-${name}-${opt === "Sì" ? "si" : "no"}`}
            onClick={() => setV(name, opt)}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              values[name] === opt ? "border-forest bg-forest text-white" : "border-border text-forest/70 hover:bg-sage"}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  if (info?.error) return <div className="flex h-screen items-center justify-center text-muted-foreground">Link non valido o scaduto.</div>;

  if (done) return (
    <div className="min-h-screen bg-sage/30 py-16">
      <Card className="mx-auto max-w-lg rounded-2xl border-border/60 p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
        <h1 className="mt-4 font-heading text-2xl font-semibold text-forest">Dati inviati con successo</h1>
        <p className="mt-2 text-muted-foreground">Grazie! I tuoi dati sono stati trasmessi a {info?.agency_name || "Assietica"}. Verrai ricontattato a breve per la proposta.</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-sage/30 py-10">
      <div className="mx-auto max-w-2xl px-4">
        <div className="mb-6 flex items-center gap-2 text-forest">
          <ShieldCheck className="h-6 w-6 text-terracotta" />
          <span className="font-heading text-lg font-semibold">{info?.agency_name || "Assietica"}</span>
        </div>
        <Card className="rounded-2xl border-border/60 p-6 sm:p-8">
          <h1 className="font-heading text-2xl font-semibold text-forest">Richiesta preventivo — {info.schema.product_name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Compila i dati richiesti: il nostro consulente preparerà una proposta su misura. I campi con * sono obbligatori.</p>

          <div className="mt-6 space-y-7">
            {info.schema.sections.map((sec, idx) => (
              <div key={sec.title}>
                <div>
                  <h2 className="mb-3 border-b border-border/60 pb-1 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">{sec.title}</h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {sec.fields.map(renderField)}
                  </div>
                </div>

                {/* Blocco garanzie Infortuni dopo la sezione Dati del rischio */}
                {sec.title === "Dati del rischio" && inf && infCat && (
                  <div className="mt-6">
                    <h2 className="mb-2 border-b border-border/60 pb-1 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">Coperture desiderate</h2>
                    <p className="mb-3 text-[11px] text-muted-foreground">Indica massimali e diarie di interesse: velocizzeranno la preparazione del preventivo (Invalidità Permanente e Morte sono sempre incluse).</p>
                    <div className="space-y-2.5">
                      {infCat.coverages.map((c) => {
                        const key = `garanzia_${c.code}`;
                        const sel = c.obbligatoria || values[key] != null;
                        return (
                          <div key={c.code} className="rounded-lg border border-border/60 p-3">
                            <div className="flex items-center justify-between gap-3">
                              <label className="flex items-center gap-3 text-sm">
                                <Checkbox data-testid={`intake-inf-cov-${c.code}`} checked={sel} disabled={c.obbligatoria}
                                  onCheckedChange={(v) => {
                                    if (c.obbligatoria) return;
                                    setValues((s) => {
                                      const nv = { ...s };
                                      if (v) nv[key] = c.massimali[Math.floor(c.massimali.length / 2)] || c.massimali[0];
                                      else delete nv[key];
                                      return nv;
                                    });
                                  }} />
                                <span className="text-forest">{inf.garanzia_labels[c.code]}{c.obbligatoria && <span className="text-[10px] text-muted-foreground"> (inclusa)</span>}</span>
                              </label>
                              {sel && (
                                <Select value={String(values[key] ?? "")} onValueChange={(v) => setV(key, Number(v))}>
                                  <SelectTrigger data-testid={`intake-inf-massimale-${c.code}`} className="h-9 w-40"><SelectValue /></SelectTrigger>
                                  <SelectContent>{c.massimali.map((m) => <SelectItem key={m} value={String(m)}>{c.tipo === "somma" ? eur(m) : `${eur(m)}/gg`}</SelectItem>)}</SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Blocco tecnico RC subito dopo i dati dell'ente */}
                {idx === 0 && rc && rcSection && (
                  <div className="mt-7 space-y-6">
                    <div>
                      <h2 className="mb-3 border-b border-border/60 pb-1 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">Dati del rischio</h2>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <YesNo name="figure_sanitarie" label="L'ente impiega figure sanitarie (OSS, infermieri, medici)?" />

                        <div className="sm:col-span-2 space-y-1.5">
                          <Label>Ambito di rischio<span className="text-terracotta"> *</span></Label>
                          <Select value={values.ambito || "non_sanitaria"} onValueChange={onAmbito}>
                            <SelectTrigger data-testid="intake-rc-ambito"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="non_sanitaria">RC Non Sanitaria / Socio-sanitaria</SelectItem>
                              <SelectItem value="sanitaria">RC Rischio Sanitario</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="sm:col-span-2 space-y-1.5">
                          <Label>Settore Principale<span className="text-terracotta"> *</span></Label>
                          <Select value={values.settore_principale || ""} onValueChange={onSettore}>
                            <SelectTrigger data-testid="intake-rc-settore"><SelectValue placeholder="Seleziona tipologia cliente..." /></SelectTrigger>
                            <SelectContent>
                              {(values.ambito === "sanitaria"
                                ? sanSettori.map((s) => ({ v: s, l: s }))
                                : nonsanSettori.map((s) => ({ v: s.label, l: s.label }))
                              ).map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>

                        {values.ambito === "sanitaria" && (
                          <div className="sm:col-span-2 space-y-1.5">
                            <Label>Tipo Principale<span className="text-terracotta"> *</span></Label>
                            <Select value={values.tipo_principale || ""} onValueChange={onTipo}>
                              <SelectTrigger data-testid="intake-rc-tipo-san"><SelectValue placeholder="Seleziona attività sanitaria..." /></SelectTrigger>
                              <SelectContent>{sanTipi.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {values.ambito === "non_sanitaria" && settoreMode === "tesserati" && (
                          <div className="sm:col-span-2 space-y-1.5">
                            <Label>Tipo Principale<span className="text-terracotta"> *</span></Label>
                            <Select value={values.tipo_principale || ""} onValueChange={onTipo}>
                              <SelectTrigger data-testid="intake-rc-tipo-sport"><SelectValue placeholder="Seleziona attività sportiva..." /></SelectTrigger>
                              <SelectContent>{sportTipi.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {values.ambito === "non_sanitaria" && settoreMode === "anime" && (
                          <div className="sm:col-span-2 space-y-1.5">
                            <Label>Tipo Principale</Label>
                            <div data-testid="intake-rc-tipo-parrocchie" className="rounded-md border border-border/60 bg-sage/30 px-3 py-2 text-sm text-forest">{rc.nonsan_parrocchie_tipo}</div>
                          </div>
                        )}
                        {values.ambito === "non_sanitaria" && settoreMode === "choice" && (
                          <div className="sm:col-span-2 space-y-1.5">
                            <Label>Tipo Principale (attività)<span className="text-terracotta"> *</span></Label>
                            <SearchableSelect testid="intake-rc-tipo-generic" options={genericTipi}
                              value={values.tipo_principale || ""} onChange={onTipo} placeholder="Seleziona o cerca l'attività..." />
                          </div>
                        )}

                        {values.ambito === "non_sanitaria" && settoreMode === "choice" && (
                          <div className="space-y-1.5">
                            <Label>Parametro dimensionale</Label>
                            <Select value={values.parametro} onValueChange={(v) => setV("parametro", v)}>
                              <SelectTrigger data-testid="intake-rc-parametro"><SelectValue /></SelectTrigger>
                              <SelectContent>{Object.entries(rcSection.parameters).filter(([k]) => k === "fatturato" || k === "soci").map(([k, p]) => <SelectItem key={k} value={k}>{p.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          <Label>{rcParamMeta?.label || "Valore"}<span className="text-terracotta"> *</span></Label>
                          <Input data-testid="intake-rc-valore" type="number" value={values.parametro_valore || ""}
                            onChange={(e) => setV("parametro_valore", e.target.value)} placeholder="0" />
                        </div>

                        <div className="space-y-1.5">
                          <Label>Massimale desiderato<span className="text-terracotta"> *</span></Label>
                          <Select value={String(values.massimale || "")} onValueChange={(v) => setV("massimale", v)}>
                            <SelectTrigger data-testid="intake-rc-massimale"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>{rcSection.massimali.map((m) => <SelectItem key={m} value={String(m)}>{eur(m)}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h2 className="mb-3 border-b border-border/60 pb-1 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">Garanzie di interesse</h2>
                      <p className="mb-3 text-[11px] text-muted-foreground">Seleziona le coperture aggiuntive che ti interessano (facoltative).</p>
                      <div className="space-y-2.5">
                        {rc.coverages.map((c) => {
                          const eligible = !c.coverage_code || isCovEligible(c.coverage_code);
                          return (
                            <div key={c.name}>
                              <label className={`flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2.5 text-sm ${eligible ? "" : "opacity-60"}`}>
                                <Checkbox data-testid={`intake-cov-${c.name}`} disabled={!eligible} checked={values[c.name] === "Sì"}
                                  onCheckedChange={(v) => setV(c.name, v ? "Sì" : "")} />
                                <span className="text-forest">{c.label}</span>
                              </label>
                              {!eligible && (
                                <p data-testid={`intake-cov-${c.name}-blocked`} className="mt-1 pl-3 text-[11px] text-amber-700">
                                  {values.settore_principale ? (covElig[c.coverage_code]?.message || "Garanzia non disponibile per il soggetto selezionato.") : "Seleziona prima il Settore Principale."}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {info.schema.idd && (
              <div data-testid="intake-idd-section">
                <h2 className="mb-3 border-b border-border/60 pb-1 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">Valutazione di adeguatezza (IDD)</h2>
                <IddDeclaration value={values.idd} onChange={(v) => setV("idd", v)} testidPrefix="intake-idd" />
              </div>
            )}

            {info.schema.sinistri && (
              <div data-testid="intake-sinistri-section">
                <h2 className="mb-3 border-b border-border/60 pb-1 font-heading text-sm font-semibold uppercase tracking-wide text-forest/70">Dichiarazione sinistri</h2>
                <SinistriDeclaration value={values.sinistri} onChange={(s) => setV("sinistri", s)}
                  periodoAnni={info.schema.sinistri.periodo_anni} testidPrefix="intake-sinistri" />
              </div>
            )}
          </div>

          <PrivacyConsent checked={privacyOk} onCheckedChange={setPrivacyOk} testid="intake-privacy-consent" className="mt-8" />
          <Button data-testid="intake-submit-button" onClick={submit} disabled={sending || !privacyOk}
            className="mt-4 h-12 w-full gap-2 rounded-xl bg-forest hover:bg-forest-hover">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Invia i miei dati"}
          </Button>
          <p className="mt-3 text-center text-[11px] text-muted-foreground">I dati saranno trattati per la sola finalità di elaborazione del preventivo assicurativo.</p>
        </Card>
        <div className="mt-6"><ComplianceNotice /></div>
        <LegalFooterLinks className="mt-4" />
      </div>
    </div>
  );
}
